// src/components/cofounder/CoFounderOnboarding.js
import React, { useState } from 'react';
import { Brain, ArrowRight, Shield, BarChart2, Zap } from 'lucide-react';
import { useCoFounder } from '../../context/CoFounderContext';

const CoFounderOnboarding = () => {
  const { completeOnboarding } = useCoFounder();
  const [step, setStep] = useState(0);
  const [persona, setPersona] = useState('professional');

  const steps = [
    {
      title: 'Meet your coFounder',
      description: 'An AI business partner that understands your business deeply and helps you make better decisions.',
      icon: Brain,
      features: [
        { icon: BarChart2, label: 'Analyzes your real business data' },
        { icon: Zap, label: 'Takes actions with your approval' },
        { icon: Shield, label: 'Proactively alerts you to opportunities and risks' }
      ]
    },
    {
      title: 'Choose your style',
      description: 'How would you like coFounder to communicate with you?',
      options: [
        { value: 'professional', label: 'Professional', desc: 'Clear, structured, business-focused' },
        { value: 'friendly', label: 'Friendly', desc: 'Warm, conversational, encouraging' },
        { value: 'concise', label: 'Concise', desc: 'Brief, direct, no fluff' }
      ]
    }
  ];

  const handleComplete = () => {
    completeOnboarding({ persona });
  };

  const currentStep = steps[step];

  return (
    <div className="flex items-center justify-center min-h-[400px] p-6">
      <div className="max-w-md w-full">
        {step === 0 ? (
          <div className="text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#190192] to-[#4f46e5] flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Brain size={36} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{currentStep.title}</h2>
            <p className="text-gray-500 mb-8">{currentStep.description}</p>

            <div className="space-y-3 mb-8">
              {currentStep.features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <div className="w-8 h-8 rounded-lg bg-[#190192] bg-opacity-10 flex items-center justify-center">
                      <Icon size={16} className="text-[#190192]" />
                    </div>
                    <span className="text-sm text-gray-700">{f.label}</span>
                  </div>
                );
              })}
            </div>

            <button onClick={() => setStep(1)}
                    className="w-full py-3 bg-[#190192] text-white rounded-xl font-medium hover:bg-[#0f0075] transition-colors flex items-center justify-center gap-2">
              Get Started <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-2">{currentStep.title}</h2>
            <p className="text-gray-500 mb-6">{currentStep.description}</p>

            <div className="space-y-3 mb-8">
              {currentStep.options.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPersona(opt.value)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
                    persona === opt.value
                      ? 'border-[#190192] bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium text-gray-800">{opt.label}</span>
                  <p className="text-sm text-gray-500 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>

            <button onClick={handleComplete}
                    className="w-full py-3 bg-[#190192] text-white rounded-xl font-medium hover:bg-[#0f0075] transition-colors">
              Start using coFounder
            </button>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex justify-center gap-2 mt-6">
          {steps.map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full transition-colors ${
              i === step ? 'bg-[#190192]' : 'bg-gray-300'
            }`} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CoFounderOnboarding;
